import { useCallback, useEffect, useRef, useState } from 'react';

interface AutosaveOptions {
  /** True when there are unsaved changes. */
  dirty: boolean;
  /** Persist the draft. Must reject on failure so the bar can say so. */
  onSave: () => Promise<void>;
  /**
   * Autosave writes drafts only — publishing stays explicit, so a published
   * artefact never changes under its readers because someone typed.
   */
  enabled: boolean;
  intervalMs?: number;
}

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Autosaves every 3s while dirty and reports when it last succeeded.
 *
 * `savedAt` ticks a re-render every 10s so "saved 12s ago" stays honest
 * without a toast per keystroke.
 */
export function useAutosave({ dirty, onSave, enabled, intervalMs = 3000 }: AutosaveOptions) {
  const [state, setState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [, forceTick] = useState(0);

  // Keep the latest callback without restarting the timer on every render.
  const saveRef = useRef(onSave);
  saveRef.current = onSave;
  const inFlight = useRef(false);

  const run = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setState('saving');
    try {
      await saveRef.current();
      setSavedAt(Date.now());
      setState('saved');
    } catch {
      // Surfaced in the header; the caller's own error handling still applies.
      setState('error');
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !dirty) return;
    const timer = setTimeout(run, intervalMs);
    return () => clearTimeout(timer);
  }, [enabled, dirty, intervalMs, run, state]);

  // Re-render so the relative timestamp advances.
  useEffect(() => {
    if (savedAt === null) return;
    const tick = setInterval(() => forceTick((n) => n + 1), 10_000);
    return () => clearInterval(tick);
  }, [savedAt]);

  return { state, savedAt, saveNow: run };
}

/** "saved 12s ago" / "saved 4m ago". */
export function formatSavedAt(savedAt: number | null): string {
  if (savedAt === null) return '';
  const seconds = Math.floor((Date.now() - savedAt) / 1000);
  if (seconds < 5) return 'saved just now';
  if (seconds < 60) return `saved ${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `saved ${minutes}m ago`;
  return `saved ${Math.floor(minutes / 60)}h ago`;
}
